import React, { useEffect } from 'react';
import '../../../../../styles/fonts.scss';
import { useNavigate, useParams } from 'react-router-dom';
import {
	CommandType,
	ContentConverterEditor, waitForFontsApplied,
	WorkshopTemplateEditor,
} from '@start-set/wakadoo-editor-core';
import IEditorCommand from '@start-set/wakadoo-editor-core/mutations/commands/IEditorCommand';
import { IAIGenerationHintState, AnyComponentStructure } from '@start-set/wakadoo-editor-core/Types';
import { notificationError } from '../../../../../components/Notifications/callNotifcation';
import { closeAllModals, resetAllResponses } from '../../../../../features/ai/model/slice/aiSlice';
import { useAppDispatch } from '../../../redux';
import { IWorkshopTemplateData } from '../../../../../entities/template/types';
import { workshopTemplateAPI } from '../../../../../entities/template/workshop/template/api/api';
import css from '../../styles/editor.module.scss';
import {
	addSendErrorListener,
	addSendSuccessListener,
	addStartSendListener,
	sendCommands, socketSlice,
} from '../../../../../features/websocket/model/slice';
import { TEMPLATE_TYPES } from '../../types/types';
import {
	createWorkshopModuleActions,
} from '../../../../../features/manage-workshop-module/model/create-module/slice/createWorkshopModuleSlice';
import { useUploadImage } from '../../hooks/useUploadImage';
import { useUploadXlsx } from '../../hooks/useUploadXlsx';
import { sketchAPI } from '../../../../../entities/sketch/api/api';
import { teamTemplateAPI } from '../../../../../entities/template/team/api/api';
import { fileAPI } from '../../../../../entities/file/api/api';
import { workshopModuleActions, workshopModuleApi } from '../../../../../entities/module/workshop-module';
import { moduleWindowActions } from '../../../../../entities/module/private-template-module/model/slice';
import { IWorkshopModule } from '../../../../../entities/module/workshop-module/model/types/types';
import { templatePrivateAPI } from '../../../../../entities/template/private-template/api/api';
import { onCloseAIActionsModal, onOpenAIActionsModal } from '../../../../../redux/thunk-creators/moduleWindow';

export interface IWorkshopTemplateEditorProps {
	sketchID: string;
}

const ComponentWorkshopTemplateEditor = (props: IWorkshopTemplateEditorProps) => {
	const { sketchID } = props;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const uploadImageFile = useUploadImage();
	const uploadXlsxFile = useUploadXlsx();

	const refEditorContainer: React.RefObject<HTMLDivElement> = React.createRef();

	let editor: WorkshopTemplateEditor | null = null;

	const onBackPage = () => {
		navigate('/integrator/workshop/templates');
		dispatch(closeAllModals());
		dispatch(workshopTemplateAPI.util?.invalidateTags(['templates']));
	};

	const openCreateModuleWindow = (
		fn: (showPreview: (bytes: Blob) => void) => void,
		structure: AnyComponentStructure,
	): void => {
		const showPreview = (previewBlob: Blob) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				if (reader.result) {
					dispatch(moduleWindowActions.setCreateModuleWindowPreview(reader.result as string));
				}
			};
			reader.readAsDataURL(previewBlob);
		};

		requestAnimationFrame(() => {
			dispatch(createWorkshopModuleActions.setIsWindowOpen({ editor, active: true }));
			dispatch(createWorkshopModuleActions.setStructure(structure));
		})

		fn(showPreview);
	};

	const onOpenModulesWindow = () => {
		if (editor === null || editor === undefined) {
			throw new Error('Editor is null or undefined');
		}

		requestAnimationFrame(() => {
			dispatch(workshopModuleActions.setIsModulesModalActive({ editor, active: true }));
		});
	};

	const onCloseModulesWindow = () => {
		requestAnimationFrame(() => {
			dispatch(workshopModuleActions.setIsModulesModalActive({ active: false }));
		});
	};

	const onOpenCreateCustomTableWindow = () => {
		requestAnimationFrame(() => {
			dispatch(moduleWindowActions.setIsCreateTableModalOpen(true));
		});
	};

	const sendFilteredCommands = (commands: IEditorCommand[]): void => {
		const filteredCommands = commands.filter(
			command => command.type !== CommandType.LAYER_SEQUENCES,
		);

		dispatch(sendCommands({
			commands: filteredCommands,
			templateType: TEMPLATE_TYPES.WORKSHOP,
		}));
	};

	const onDownloadPDF = async (editor: WorkshopTemplateEditor | null, template: IWorkshopTemplateData) => {
		const structure = editor?.getStructure();

		if (structure) {
			const contentConvertEditor = new ContentConverterEditor({
				isEnableMouseWheel: false,
				isCanvasHeightAdaptive: true,
				imageFileSourcePrefix: '/api/file/user'
			});
			await contentConvertEditor.setStructure({
				...template,
				root: structure.root,
			})
			await contentConvertEditor.savePdf(template.name);
		} else {
			notificationError('Ошибка', 'Не удалось найти структуру для скачивания PDF');
		}
	};

	/** Выполняет загрузку модуля */
	const loadModuleData = async (moduleId: string) => {
		try {
			const response = await dispatch(workshopModuleApi.endpoints.getWorkshopModule.initiate({ moduleId })).unwrap();
			return response;
		} catch (error) {
			notificationError('Ошибка', 'Не удалось загрузить структуру модуля');
			console.log('Ошибка: загрузки модуля: ', error);
			return null;
		}
	};

	/** Выполняет ижнект модуля */
	const injectModuleStructure = (moduleData: IWorkshopModule | null, editor: WorkshopTemplateEditor | null, closeModulesWindow: () => void) => {
		if (moduleData) {
			closeModulesWindow();
			editor?.injectModule(moduleData.rootComponent);
		} else {
			console.error('Структура модуля не найдена');
		}
	};

	/** Устанавливает состояние "загрузка" в редакторе */
	const setStateLoadingSaveTemplate = (editor: WorkshopTemplateEditor | null) => {
		if (editor) {
			editor.setStateLoadingSaveTemplate();
		}
	};

	/** Устанавливает состояние "успех" в редакторе */
	const setStateSuccessSaveTemplate = (editor: WorkshopTemplateEditor | null) => {
		if (editor) {
			editor.setStateSuccessSaveTemplate();
		}
	};

	/** Устанавливает состояние "ошибка" в редакторе */
	const setStateErrorSaveTemplate = (editor: WorkshopTemplateEditor | null) => {
		if (editor) {
			editor.setStateErrorSaveTemplate();
		}
	};

	/** Cохраняет превью на сервере */
	const savePreviewFile = (fileID: string) => {
		dispatch(sketchAPI.endpoints?.update.initiate({
			preview: fileID,
			sketchId: sketchID,
		}))
			.unwrap()
			.catch(error => {
				throw new Error(error);
			});
	};

	const onUpdateTemplatePreview = (previewBlob: Blob) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			if (reader.result) {
				const previewDataUrl = reader.result as string;
				dispatch(moduleWindowActions.setCreateModuleWindowPreview(previewDataUrl));

				dispatch(fileAPI.endpoints?.savePreview.initiate({
					name: `preview-${sketchID}-${new Date().toISOString()}`,
					bytes: previewDataUrl,
				}))
					.unwrap()
					.then(response => {
						savePreviewFile(response.id);
					})
					.then(() => {
						dispatch(teamTemplateAPI.util?.invalidateTags(['team']));
						dispatch(templatePrivateAPI.util?.invalidateTags(['templates']));
						dispatch(workshopTemplateAPI.util?.invalidateTags(['templates']));
					});
			}
		};
		reader.readAsDataURL(previewBlob);
	};

	useEffect(() => {
		const initEditor = async () => {
			if (refEditorContainer.current === null || refEditorContainer.current === undefined) {
				throw new Error('manipulator container not found');
			}

			const getAIGeneration = (): IAIGenerationHintState => ({
				availableGeneration: 0,
				totalGeneration: 600,
				recoveryTimeHours: 0,
			});

			try {
				const res = await dispatch(
					workshopTemplateAPI.endpoints?.get.initiate({ id: sketchID }),
				).unwrap();

				const { template, rootComponent } = res;

				dispatch(socketSlice.actions.connectWorkshopTemplate(sketchID));

				await waitForFontsApplied();

				editor = new WorkshopTemplateEditor({
					onBackPage,
					uploadXlsxFile,
					uploadImageFile,
					onOpenModulesWindow,
					onCloseModulesWindow,
					onOpenAIActionsModal,
					onCloseAIActionsModal,
					enableDebugMenu: false,
					openCreateModuleWindow,
					onUpdateTemplatePreview,
					onOpenCreateCustomTableWindow,
					getServerConnectionStatus: () => true,
					imageFileSourcePrefix: '/api/file/user',
					onGetGenerationHintStructure: getAIGeneration,
					sendCommands(commands: IEditorCommand[]): void {
						sendFilteredCommands(commands);
					},
					onDownloadPDF: () => onDownloadPDF(editor, template),
					editorContainer: refEditorContainer.current as HTMLDivElement,
					onGoToTariff: () => window.location.href = 'https://wakadoo.pro/tarif'
				});

				await editor.setStructure({
					...template,
					root: rootComponent,
				})

				dispatch(addStartSendListener({ listener: () => {
					setStateLoadingSaveTemplate(editor);
				}}));

				dispatch(addSendSuccessListener({ listener: () => {
					setStateSuccessSaveTemplate(editor);
				}}));

				dispatch(addSendErrorListener({listener: () => {
					setStateErrorSaveTemplate(editor);
				}}));

				/** Инжект модуля */
				dispatch(
					workshopModuleActions.setModuleFunc((moduleId: string) => {
						loadModuleData(moduleId)
							.then((moduleData) => {
								injectModuleStructure(moduleData, editor, onCloseModulesWindow);
							})
							.catch((error) => {
								console.error(error);
							});
					})
				);
			} catch (e) {
				notificationError('Загрузка структуры', 'Возникла непредвиденная ошибка загрузки структуры.');
				console.log(e);
			}

			return () => {
				dispatch(socketSlice.actions.disconnectWorkshopTemplate(sketchID));
				dispatch(resetAllResponses());
				editor && editor.destruct();
			};
		};

		const cleanup = initEditor();
		return () => {
			cleanup.then((fn) => fn && fn());
		};
	}, []);

	return (
		<div ref={refEditorContainer} className={`${css.editor} ${css.workshopTemplateEditor}`} />
	);
};

export const WorkshopTemplateEditorWithRouter = () => {
	const { id } = useParams();
	if (id === undefined) {
		return <p>error get sketch id</p>;
	}

	return <ComponentWorkshopTemplateEditor sketchID={id} />;
};
